import React, { useState } from "react";
import { Activity, Camera, ListChecks, ShieldOff } from "lucide-react";

import ProctoringMonitor from "./ProctoringMonitor";
import ProctoringReview from "./ProctoringReview";
import BlocksPanel from "./BlocksPanel";
import WebcamPanel from "./WebcamPanel";

/**
 * Proctoring Center — the single admin surface for exam proctoring.
 *
 *   Live     — active browser-proctored sessions (violation ticks, block)
 *   Sessions — post-hoc audit: violation timelines, recordings, verdicts
 *   Webcam   — webcam-AI evidence roster per test: everyone who launched,
 *              capture photos + metrics, no violation needed to get here
 *   Blocks   — every block lever: auto violation-blocks + admin blocks with
 *              Unblock, plus drill-in to the webcam evidence
 *
 * /proctoring-monitor and /proctoring-review remain registered as deep links
 * into the corresponding tab so existing nav entries keep working.
 */

type TabKey = "live" | "sessions" | "webcam" | "blocks";

const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: "live", label: "Live", icon: Activity },
  { key: "sessions", label: "Sessions", icon: ListChecks },
  { key: "webcam", label: "Webcam", icon: Camera },
  { key: "blocks", label: "Blocks", icon: ShieldOff },
];

const tabFromQuery = (): TabKey | null => {
  const t = new URLSearchParams(window.location.search).get("tab");
  return t === "live" || t === "sessions" || t === "webcam" || t === "blocks"
    ? t
    : null;
};

const ProctoringCenter: React.FC<{ initialTab?: TabKey }> = ({ initialTab }) => {
  const [tab, setTab] = useState<TabKey>(tabFromQuery() || initialTab || "live");

  const switchTab = (next: TabKey) => {
    setTab(next);
    // Keep the URL shareable without coupling to the router version.
    window.history.replaceState(null, "", `/proctoring?tab=${next}`);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          padding: "16px 20px 0",
        }}
      >
        <h4 style={{ margin: 0 }}>Proctoring Center</h4>
        <div style={{ display: "flex", gap: 6 }}>
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => switchTab(key)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 999,
                  cursor: "pointer",
                  border: active
                    ? "1px solid var(--brand-primary)"
                    : "1px solid var(--border-default)",
                  background: active
                    ? "var(--brand-primary)"
                    : "var(--surface-card)",
                  color: active ? "var(--text-on-brand)" : "var(--text-body)",
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                <Icon size={14} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "live" && <ProctoringMonitor />}
      {tab === "sessions" && <ProctoringReview />}
      {tab === "webcam" && <WebcamPanel />}
      {tab === "blocks" && <BlocksPanel />}
    </div>
  );
};

/** Deep-link wrappers for the legacy routes. */
export const ProctoringCenterLive: React.FC = () => (
  <ProctoringCenter initialTab="live" />
);
export const ProctoringCenterSessions: React.FC = () => (
  <ProctoringCenter initialTab="sessions" />
);

export default ProctoringCenter;
